"use client";

/**
 * Отзывы — НАТИВНЫЙ блок (дизайн как в Figma): слева карточки рейтинга по
 * источникам с оригинальными логотипами Яндекс.Карт и 2ГИС, справа — 2 свежих
 * отзыва (вперемешку по филиалам/источникам) и кнопки перехода на источник.
 * Данные парсятся в gateway (/api/v1/reviews). На главной — все города; на
 * странице города — только его филиалы (проп station).
 */

import Img from '@/components/Img';
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchReviews, type ReviewItem, type ReviewSourceLink } from "@/lib/api";
import CarouselArrow from "./CarouselArrow";
import Container from "./Container";
import SectionTitle from "./SectionTitle";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

const SRC_META: Record<string, { name: string; logo: string }> = {
  yandex: { name: "Яндекс.Карты", logo: "/icons/review/yandex.png" },
  twogis: { name: "2ГИС", logo: "/icons/review/2gis.png" },
};
// Цвета аватарок-плейсхолдеров Яндекса (_icon_1..7).
const AVATAR_COLORS: Record<string, string> = {
  "1": "#b37db3", "2": "#f29d61", "3": "#50c0e6", "4": "#6ca632",
  "5": "#f285aa", "6": "#8585cc", "7": "#5798d9",
};

function Stars({ value = 5, size = 16 }: { value?: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} viewBox="0 0 16 16" style={{ width: size, height: size }} className={i < Math.round(value) ? "fill-[#ffb800]" : "fill-[#e3e6ec]"} aria-hidden>
          <path d="M8 0.667l2.49 5.046 5.567.81-4.029 3.927.951 5.547L8 13.378 2.02 16l.95-5.547L-1.057 6.523l5.567-.81L8 0.667z" />
        </svg>
      ))}
    </div>
  );
}

function initialOf(name: string): string {
  const c = (name || "").trim()[0];
  return c ? c.toUpperCase() : "?";
}

function ReviewCard({ r }: { r: ReviewItem }) {
  const { t } = useLocale();
  const meta = SRC_META[r.source];
  const color = AVATAR_COLORS[r.avatar] || "#0039A6";
  const isUrl = r.avatar.startsWith("http");
  return (
    <article className="flex h-full flex-col gap-3 rounded-3xl bg-white p-5 sm:p-6">
      <div className="flex items-center gap-3">
        {isUrl ? (
          <Img src={r.avatar} alt={r.author} className="size-10 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full text-[16px] font-semibold text-white" style={{ background: color }}>
            {initialOf(r.author)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold leading-tight text-black">{r.author || t("reviews.guest")}</div>
          <div className="text-[13px] text-text-muted">{r.date}</div>
        </div>
        {meta && (
          <Img src={meta.logo} alt={meta.name} className="h-5 w-auto shrink-0 object-contain" title={meta.name} />
        )}
      </div>
      <Stars value={r.rating || 5} />
      <p className="line-clamp-[8] whitespace-pre-line text-[14px] leading-6 text-[#2B3350]">{r.text}</p>
    </article>
  );
}

export default function Reviews({ station }: { station?: string }) {
  const { t } = useLocale();
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [sources, setSources] = useState<ReviewSourceLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSource, setActiveSource] = useState<string>(""); // переключатель карусели

  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  const scrollBy = (d: number) => trackRef.current?.scrollBy({ left: d, behavior: "smooth" });

  useEffect(() => {
    let off = false;
    setLoading(true);
    fetchReviews(station)
      .then((d) => { if (!off) { setReviews(d.reviews); setSources(d.sources); } })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [station]);

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    el?.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => { el?.removeEventListener("scroll", updateArrows); window.removeEventListener("resize", updateArrows); };
  }, [updateArrows, reviews.length, loading, activeSource]);

  // Агрегаты по источнику: рейтинг (макс. по числу отзывов) + суммарный счётчик +
  // ссылка «все отзывы» (первый филиал источника).
  const bySource = ["yandex", "twogis"].map((key) => {
    const list = sources.filter((s) => s.source === key && s.url);
    if (list.length === 0) return null;
    const best = list.slice().sort((a, b) => b.count - a.count)[0];
    const count = list.reduce((n, s) => n + (s.count || 0), 0);
    const rating = best.rating || 5;
    return { key, meta: SRC_META[key], rating, count, url: best.url };
  }).filter(Boolean) as { key: string; meta: { name: string; logo: string }; rating: number; count: number; url: string }[];

  // Активный источник (квадратные карточки слева переключают карусель). По
  // умолчанию — первый источник, у которого есть распарсенные отзывы.
  const active = bySource.some((s) => s.key === activeSource)
    ? activeSource
    : (bySource.find((s) => reviews.some((r) => r.source === s.key))?.key || bySource[0]?.key || "");
  const shown = reviews.filter((r) => r.source === active);
  const activeCta = bySource.find((s) => s.key === active);
  const switchSource = (key: string) => { track("review_source_switch", { city: station }); setActiveSource(key); trackRef.current?.scrollTo({ left: 0 }); };

  // Блок отзывов — целиком скрываем, если отзывов нет вообще (ни Яндекс, ни
  // 2ГИС). Заголовок «Отзывы» живёт здесь же, чтобы прятался вместе с секцией;
  // во время загрузки отдаём null — без мигающего пустого заголовка.
  if (loading || reviews.length === 0) return null;

  return (
    <section id="reviews" className="bg-bg pb-[60px] lg:pb-[80px]">
      <Container>
        <SectionTitle title={t("reviews.title")} />
        <div className="mt-8 lg:mt-[24px]">
    <div className="flex flex-col gap-4 lg:flex-row lg:gap-2">
      {/* sidebar — переключатель источника карусели (Яндекс / 2ГИС) */}
      <div className="flex shrink-0 flex-row gap-2 lg:w-[160px] lg:flex-col">
        {bySource.map((s) => {
          const on = s.key === active;
          return (
            <button key={s.key} type="button" onClick={() => switchSource(s.key)} aria-pressed={on}
               className={`flex flex-1 cursor-pointer flex-col items-start justify-between gap-4 rounded-2xl bg-white p-4 text-left transition-all duration-200 ${on ? "z-10 scale-[1.03] shadow-[0_14px_32px_rgba(0,57,166,0.22)]" : "scale-100 opacity-70 shadow-[0_3px_10px_rgba(20,29,69,0.08)] hover:opacity-100"}`}>
              <div className="flex flex-col gap-2.5">
                <span className="text-[24px] font-semibold leading-[1.3] text-black">{s.rating.toFixed(1)}</span>
                <Stars value={s.rating} />
                {s.count > 0 && <span className="text-[13px] text-text-muted">{t("reviews.count", { n: String(s.count) })}</span>}
              </div>
              <Img src={s.meta.logo} alt={s.meta.name} className="h-7 w-auto object-contain" />
            </button>
          );
        })}
      </div>

      {/* reviews carousel (side arrows — наш стиль) + source buttons */}
      <div className="flex flex-1 flex-col gap-4" style={{ minWidth: 0 }}>
        <div className="relative flex-1">
          <div ref={trackRef} className="flex h-full min-h-[300px] snap-x items-stretch gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {shown.map((r, i) => (
              <div key={`${r.source}-${r.station}-${i}`} className="w-[300px] shrink-0 self-stretch snap-start sm:w-[360px]">
                <ReviewCard r={r} />
              </div>
            ))}
            {shown.length === 0 && (
              <div className="flex w-[300px] shrink-0 self-stretch snap-start flex-col items-center justify-center gap-2 rounded-3xl bg-white p-6 text-center text-text-muted sm:w-[360px]">
                <span className="text-[15px] font-medium">{t("reviews.soon")}</span>
              </div>
            )}
            {/* Последняя карточка карусели — переход на активный источник. */}
            {activeCta && (
              <a key={`cta-${activeCta.key}`} href={activeCta.url} target="_blank" rel="noopener noreferrer"
                 onClick={() => track("review_source_click", { city: station })}
                 className="flex w-[300px] shrink-0 self-stretch snap-start flex-col items-center justify-center gap-4 rounded-3xl bg-white p-6 text-center transition hover:shadow-[var(--shadow-card)] sm:w-[360px]">
                <Img src={activeCta.meta.logo} alt={activeCta.meta.name} className="h-9 w-auto object-contain" />
                <span className="text-[17px] font-semibold leading-snug text-[#141D45]">{t("reviews.allOn", { src: activeCta.meta.name })}</span>
                <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary">
                  {t("btn.open")}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5.5L15.5 12L9 18.5" /></svg>
                </span>
              </a>
            )}
          </div>
          {canLeft && <CarouselArrow direction="left" ariaLabel={t("a11y.prev")} onClick={() => scrollBy(-380)} className="!rounded-full absolute left-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]" />}
          {canRight && <CarouselArrow direction="right" ariaLabel={t("a11y.next")} onClick={() => scrollBy(380)} className="!rounded-full absolute right-0 top-1/2 z-10 -translate-y-1/2 shadow-[0_8px_24px_rgba(20,29,69,0.18)]" />}
        </div>
      </div>
    </div>
        </div>
      </Container>
    </section>
  );
}
